import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  OverloadMemoListParams,
  OverloadMemoListResponse,
  OverloadMemoModel,
  OverloadMemoPayload,
} from '../../models/overload-memo/overload-memo.model';

@Injectable({
  providedIn: 'root',
})
export class OverloadMemoService {
  private apiUrl = '/overload_memos';

  #http = inject(HttpClient);

  getOverloadMemos(params?: OverloadMemoListParams): Observable<OverloadMemoListResponse> {
    return this.#http.get<unknown>(this.apiUrl, {
      params: this.buildListParams(params),
    }).pipe(
      map((res) => this.normalizeListResponse(res, params)),
    );
  }

  getOverloadMemo(id: number): Observable<OverloadMemoModel> {
    return this.#http.get<OverloadMemoModel>(`${this.apiUrl}/${id}`);
  }

  createOverloadMemo(payload: OverloadMemoPayload): Observable<OverloadMemoModel> {
    return this.#http.post<OverloadMemoModel>(this.apiUrl, payload);
  }

  regenerateOverloadMemo(id: number, payload: OverloadMemoPayload): Observable<OverloadMemoModel> {
    return this.#http.post<OverloadMemoModel>(`${this.apiUrl}/${id}/regenerate`, payload);
  }

  submitOverloadMemo(id: number): Observable<OverloadMemoModel> {
    return this.#http.post<OverloadMemoModel>(`${this.apiUrl}/${id}/submit`, {});
  }

  certifyOverloadMemo(id: number): Observable<OverloadMemoModel> {
    return this.#http.post<OverloadMemoModel>(`${this.apiUrl}/${id}/certify`, {});
  }

  rejectOverloadMemo(id: number, comment: string): Observable<OverloadMemoModel> {
    return this.#http.post<OverloadMemoModel>(`${this.apiUrl}/${id}/reject`, {
      overload_memo: { comment },
    });
  }

  downloadOverloadMemo(id: number): Observable<Blob> {
    return this.#http.get(`${this.apiUrl}/${id}/download`, {
      responseType: 'blob',
    });
  }

  private buildListParams(params?: OverloadMemoListParams): HttpParams {
    let httpParams = new HttpParams();
    const supportedParams: (keyof OverloadMemoListParams)[] = [
      'academic_term_id',
      'academic_year',
      'status',
      'keywords',
      'page',
      'limit',
      'sort',
      'direction',
    ];

    supportedParams.forEach((key) => {
      const value = params?.[key];
      if (value != null && value !== '') {
        httpParams = httpParams.set(key, String(value));
      }
    });

    return httpParams;
  }

  private normalizeListResponse(
    res: unknown,
    params?: OverloadMemoListParams,
  ): OverloadMemoListResponse {
    if (res !== null && typeof res === 'object' && !Array.isArray(res)) {
      const response = res as Record<string, unknown>;

      if (Array.isArray(response.data)) {
        return {
          data: response.data as OverloadMemoModel[],
          page: Number(response.page ?? params?.page ?? 1),
          limit: Number(response.limit ?? params?.limit ?? 10),
          total: Number(response.total ?? response.data.length),
          total_pages: Number(response.total_pages ?? response.total_page ?? 1),
        };
      }
    }

    const data = Array.isArray(res) ? res as OverloadMemoModel[] : [];
    const limit = params?.limit ?? 10;

    return {
      data,
      page: params?.page ?? 1,
      limit,
      total: data.length,
      total_pages: Math.max(1, Math.ceil(data.length / limit)),
    };
  }
}
